import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { Product, ProductAttributeDefinition, ProductCategory } from '../types/Product';
import { archiveProduct, getProductById, getProductCategories, productService, removeProductAttachment, setProductPrimaryImage, updateProduct, updateProductStatus, uploadProductAttachment } from '../services/productService';
import { CategorySelect } from '../../catalog/CategorySelect';
import { CatalogMediaPanel } from '../../catalog/CatalogMediaPanel';
import { formatAttributeValue, ProductAttributeFields } from './ProductAttributeFields';
import { formatMoney } from '../../invoices/invoiceForm';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

const parseNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatAmount = (value?: number) => (value === undefined ? '-' : String(value));

export const ProductDetails: React.FC = () => {
  const { t } = useTranslation(['products', 'common']);
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const currency = useTenantCurrency();
  const [item, setItem] = React.useState<Product | null>(null);
  const [categories, setCategories] = React.useState<ProductCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: '',
    displayName: '',
    description: '',
    categoryId: '',
    code: '',
    barcode: '',
    costPrice: '',
    markupPercent: '',
    salePrice: '',
    taxPercent: '',
    stockQuantity: '0',
    unit: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [definitions, setDefinitions] = React.useState<ProductAttributeDefinition[]>([]);
  const [attributes, setAttributes] = React.useState<Record<string, string>>({});
  const [uploadingMedia, setUploadingMedia] = React.useState(false);
  const canUpdate = hasPermission(PermissionScopes.productsUpdate);
  const canDelete = hasPermission(PermissionScopes.productsDelete);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [product, productCategories] = await Promise.all([
          id?.trim() ? getProductById(id) : Promise.resolve(null),
          getProductCategories(),
        ]);
        setItem(product);
        setCategories(productCategories);
        if (product) {
          setForm({
            name: product.name,
            displayName: product.displayName || '',
            description: product.description || '',
            categoryId: product.categoryId,
            code: product.code || '',
            barcode: product.barcode || '',
            costPrice: product.costPrice === undefined ? '' : String(product.costPrice),
            markupPercent: product.markupPercent === undefined ? '' : String(product.markupPercent),
            salePrice: product.salePrice === undefined ? '' : String(product.salePrice),
            taxPercent: product.taxPercent === undefined ? '' : String(product.taxPercent),
            stockQuantity: String(product.stockQuantity ?? 0),
            unit: product.unit || '',
          });
          setAttributes(product.attributes);
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [id]);

  const categoryIdForFields = editMode ? form.categoryId : item?.categoryId;

  React.useEffect(() => {
    if (!categoryIdForFields) {
      setDefinitions([]);
      return;
    }

    let cancelled = false;
    void productService.getAttributes(categoryIdForFields, true)
      .then(items => {
        if (cancelled) return;
        setDefinitions(items);
        setAttributes(prev => {
          const next: Record<string, string> = {};
          items.forEach(definition => {
            if (prev[definition.id]) next[definition.id] = prev[definition.id];
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) setDefinitions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryIdForFields]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'products' });
    if (!form.categoryId) nextErrors.categoryId = t('categoryRequired', { ns: 'products' });
    definitions.forEach(definition => {
      if (definition.isRequired && !(attributes[definition.id] || '').trim()) {
        nextErrors[definition.id] = t('attributeRequired', { ns: 'products', name: definition.name });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSave = async () => {
    if (!id || !validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await updateProduct(id, {
        name: form.name.trim(),
        displayName: form.displayName.trim() || undefined,
        clearDisplayName: !form.displayName.trim(),
        description: form.description.trim(),
        categoryId: form.categoryId,
        code: form.code.trim() || undefined,
        clearCode: !form.code.trim(),
        barcode: form.barcode.trim() || undefined,
        clearBarcode: !form.barcode.trim(),
        costPrice: parseNumber(form.costPrice),
        clearCostPrice: !form.costPrice.trim(),
        markupPercent: parseNumber(form.markupPercent),
        clearMarkupPercent: !form.markupPercent.trim(),
        salePrice: parseNumber(form.salePrice),
        clearSalePrice: !form.salePrice.trim(),
        taxPercent: parseNumber(form.taxPercent),
        clearTaxPercent: !form.taxPercent.trim(),
        stockQuantity: parseNumber(form.stockQuantity) ?? 0,
        unit: form.unit.trim() || undefined,
        clearUnit: !form.unit.trim(),
        attributes: Object.fromEntries(definitions.map(definition => [definition.id, attributes[definition.id]?.trim() || null])),
      });
      if (!updated) {
        setGeneralError(t('failedToSave', { ns: 'products' }));
        return;
      }
      setItem(updated);
      setAttributes(updated.attributes);
      setEditMode(false);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'products' }));
    } finally {
      setSaving(false);
    }
  };

  const onArchiveOrRestore = async () => {
    if (!id || !item) return;
    setSaving(true);
    setGeneralError(null);
    try {
      if (item.status === 'archived') {
        const updated = await updateProductStatus(id, 'active');
        if (updated) setItem(updated);
      } else {
        await archiveProduct(id);
        const updated = await getProductById(id);
        if (updated) setItem(updated);
      }
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToArchive', { ns: 'products' }));
    } finally {
      setSaving(false);
    }
  };

  const applyMediaUpdate = (updated: Product | null, fallback: string) => {
    if (!updated) {
      setGeneralError(fallback);
      return;
    }
    setItem(updated);
  };

  const onUploadAttachment = async (file: File, setAsPrimary: boolean) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await uploadProductAttachment(id, file, setAsPrimary);
      applyMediaUpdate(updated, t('failedToUploadAttachment', { ns: 'products' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToUploadAttachment', { ns: 'products' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onRemoveAttachment = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await removeProductAttachment(id, attachmentId);
      applyMediaUpdate(updated, t('failedToRemoveAttachment', { ns: 'products' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToRemoveAttachment', { ns: 'products' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  const onSetPrimaryImage = async (attachmentId: string) => {
    if (!id) return;
    setUploadingMedia(true);
    setGeneralError(null);
    try {
      const updated = await setProductPrimaryImage(id, attachmentId);
      applyMediaUpdate(updated, t('failedToSetDefaultPhoto', { ns: 'products' }));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSetDefaultPhoto', { ns: 'products' }));
      throw error;
    } finally {
      setUploadingMedia(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingItem', { ns: 'products' })} /></div>;
  }

  if (!item) {
    return <div>{t('notFound', { ns: 'products' })}</div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'products' }), path: '/products' },
        { label: item.name, path: `/products/${item.id}` },
      ]} />
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{item.name}</h2>
        <div className={styles.headerActions}>
          {canDelete ? (
            <button type="button" className="btn btn-secondary" onClick={onArchiveOrRestore} disabled={saving}>
              {item.status === 'archived' ? t('restore', { ns: 'products' }) : t('archive', { ns: 'products' })}
            </button>
          ) : null}
          {canUpdate && !editMode ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setAttributes(item.attributes);
                setEditMode(true);
              }}
            >
              {t('edit', { ns: 'common' })}
            </button>
          ) : null}
          {editMode ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setAttributes(item.attributes);
                  setEditMode(false);
                }}
                disabled={saving}
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>{t('save', { ns: 'products' })}</button>
            </>
          ) : null}
        </div>
      </div>
      {generalError ? <div className="error">{generalError}</div> : null}

      <CatalogMediaPanel
        ns="products"
        itemName={item.name}
        attachments={item.attachments}
        canUpdate={canUpdate}
        busy={saving || uploadingMedia}
        onUpload={onUploadAttachment}
        onRemove={onRemoveAttachment}
        onSetPrimary={onSetPrimaryImage}
      />

      {editMode ? (
        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>{t('name', { ns: 'products' })}</label>
            <input className={styles.input} value={form.name} placeholder={t('namePlaceholder', { ns: 'products' })} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
            {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('displayName', { ns: 'products' })}</label>
            <input className={styles.input} value={form.displayName} onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))} />
          </div>
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label className={styles.label}>{t('description', { ns: 'products' })}</label>
            <textarea className={styles.textarea} value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
            <div className={styles.fieldHint}>{t('descriptionHint', { ns: 'products' })}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('category', { ns: 'products' })}</label>
            <CategorySelect
              className={styles.input}
              categories={categories}
              value={form.categoryId}
              onChange={categoryId => setForm(prev => ({ ...prev, categoryId }))}
            />
            <div className={styles.fieldHint}>{t('categoryHint', { ns: 'products' })}</div>
          </div>
          <ProductAttributeFields
            definitions={definitions}
            values={attributes}
            errors={errors}
            onChange={(attributeId, value) => setAttributes(prev => ({ ...prev, [attributeId]: value }))}
          />
          <div className={styles.field}>
            <label className={styles.label}>{t('code', { ns: 'products' })}</label>
            <input className={styles.input} value={form.code} onChange={event => setForm(prev => ({ ...prev, code: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('barcode', { ns: 'products' })}</label>
            <input className={styles.input} value={form.barcode} onChange={event => setForm(prev => ({ ...prev, barcode: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('unit', { ns: 'products' })}</label>
            <input className={styles.input} value={form.unit} placeholder={t('unitPlaceholder', { ns: 'products' })} onChange={event => setForm(prev => ({ ...prev, unit: event.target.value }))} />
            <div className={styles.fieldHint}>{t('unitHint', { ns: 'products' })}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('costPrice', { ns: 'products' })} ({currency})</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.costPrice} onChange={event => setForm(prev => ({ ...prev, costPrice: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('markupPercent', { ns: 'products' })}</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.markupPercent} onChange={event => setForm(prev => ({ ...prev, markupPercent: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('salePrice', { ns: 'products' })} ({currency})</label>
            <input className={styles.input} type="number" min="0" step="0.01" value={form.salePrice} onChange={event => setForm(prev => ({ ...prev, salePrice: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('taxPercent', { ns: 'products' })}</label>
            <input className={styles.input} type="number" min="0" max="100" step="0.01" value={form.taxPercent} onChange={event => setForm(prev => ({ ...prev, taxPercent: event.target.value }))} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('stockQuantity', { ns: 'products' })}</label>
            <input className={styles.input} type="number" min="0" step="1" value={form.stockQuantity} onChange={event => setForm(prev => ({ ...prev, stockQuantity: event.target.value }))} />
          </div>
        </div>
      ) : (
        <div className={styles.detailsGrid}>
          <div>
            <p className={styles.detailLabel}>{t('displayName', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.displayName || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('category', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.categoryName || '-'}</p>
          </div>
          {definitions.map(definition => (
            <div key={definition.id}>
              <p className={styles.detailLabel}>{definition.name}</p>
              <p className={styles.detailValue}>
                {formatAttributeValue(
                  definition,
                  item.attributes[definition.id],
                  t('yes', { ns: 'products' }),
                  t('no', { ns: 'products' }),
                )}
              </p>
            </div>
          ))}
          <div className={styles.detailItemWide}>
            <p className={styles.detailLabel}>{t('description', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.description || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('code', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.code || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('barcode', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.barcode || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('costPrice', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{formatMoney(item.costPrice, currency)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('markupPercent', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{formatAmount(item.markupPercent)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('salePrice', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{formatMoney(item.salePrice, currency)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('taxPercent', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{formatAmount(item.taxPercent)}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('stockQuantity', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.stockQuantity}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('unit', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.unit || '-'}</p>
          </div>
          <div>
            <p className={styles.detailLabel}>{t('status', { ns: 'products' })}</p>
            <p className={styles.detailValue}>{item.status === 'archived' ? t('statusArchived', { ns: 'products' }) : t('statusActive', { ns: 'products' })}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
